/**
 * Supabase client, shared by frontend (admin) and atlas (client).
 *
 * Canonical single source of truth: carries the noopLock SES workaround
 * that Cloudflare Pages needs (lockdown-install.js strips the Web Locks
 * API intrinsics Supabase v2 reaches for via navigator.locks). Atlas used
 * to lack this and carried a latent Cloudflare bug.
 *
 * `import.meta.env.VITE_*` is inlined per consuming app at build time, so
 * each app gets its own Supabase project/keys without any shared state.
 */
import { createClient } from '@supabase/supabase-js'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY

export const SUPABASE_CONFIGURED = Boolean(SUPABASE_URL && SUPABASE_ANON_KEY)

if (!SUPABASE_CONFIGURED) {
  console.warn('[supabase] VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY not set, auth disabled')
}

// Cloudflare Pages runs SES (lockdown-install.js) which removes Web Locks API
// intrinsics that Supabase v2 uses internally via navigator.locks. Providing
// a custom lock function bypasses navigator.locks entirely.
const noopLock = async <T>(_name: string, _timeout: number, fn: () => Promise<T>): Promise<T> => fn()

export const supabase = createClient(
  SUPABASE_URL || 'https://placeholder.supabase.co',
  SUPABASE_ANON_KEY || 'placeholder-anon-key',
  {
    auth: {
      lock: noopLock,
    },
  },
)
